import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import APIServices from "../services/APIServices";
import Loader from "../components/Loader";
import ResponsivePagination from "react-responsive-pagination";
import "react-responsive-pagination/themes/classic.css";
import appToast, { TOAST_TYPE } from "../utils/AppToast";
import {
  capitalizeFirstLetter,
  getformattedPhoneNumber,
  PAGE_LIMIT,
  selectLoader,
} from "../utils/Helpers";
import SidebarModal from "../components/SidebarModal";
import UserForm from "./sidebars/UserForm";
import { useAuth } from "../contexts/AuthContext";
import AddEditUserPrompt from "./sidebars/AddEditUserPrompt";

const UserPromptsList = () => {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState(null);
  const [promptList, setPromptList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1); // Default to page 1
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [promptId, setAdminId] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    const handler = setTimeout(() => {
      fetchPromptList(currentPage, searchQuery);
    }, 300);

    // Cleanup function to clear the timeout if dependencies change before delay
    return () => clearTimeout(handler);
  }, [searchQuery]);

  const fetchPromptList = async (page, showLoader = true) => {
    if (showLoader) {
      setTableLoading(true);
    }
    try {
      const response = await APIServices.get(`/prompt`);
      if (response) {
        // setCurrentPage(response.currentPage);
        // setTotalPages(response.totalPages);
        setPromptList(response);
      }
    } catch (err) {
      setError("Failed to fetch blog categories.");
      console.error("Error fetching blog categories:", err);
    } finally {
      setLoading(false);
      setTableLoading(false);
    }
  };

  const handlePageChange = useCallback(
    (page) => {
      if (page && page !== currentPage) {
        fetchPromptList(page);
      }
    },
    [currentPage]
  );

  if (loading) {
    return <Loader />;
  }

  const handleToggle = async (promptId, currentStatus) => {
    if (!isSuperAdmin) {
      appToast("Only super admins can change the status.", TOAST_TYPE.ERROR);
      return;
    }

    // Prevent turning off the default prompt without selecting another
    if (currentStatus) {
      appToast("At least one prompt should be default.", TOAST_TYPE.WARNING);
      return;
    }

    try {
      // Optimistically update UI: Set the selected prompt to true, and others to false
      setPromptList((prevList) =>
        prevList.map((prompt) => ({
          ...prompt,
          isDefault: prompt._id === promptId,
        }))
      );

      // Make API call to update the default prompt
      const response = await APIServices.put(`/prompt/${promptId}`, {
        isDefault: true,
      });

      if (response.message) {
        appToast(response.message, TOAST_TYPE.SUCCESS);
        fetchPromptList(currentPage, false); // Refresh without table loader
      }
    } catch (error) {
      console.error("Error updating default status:", error);
      appToast("Failed to update default status.", TOAST_TYPE.ERROR);
      // Revert UI state by re-fetching
      fetchPromptList(currentPage, false);
    }
  };

  const closeAndUpdateList = () => {
    fetchPromptList(currentPage);
    setModalOpen(false);
  };
  const handleDelete = async (promptId) => {
    if (window.confirm("Are you sure you want to delete this Prompt?")) {
      try {
        let response = await APIServices.delete(`/prompt/${promptId}`);
        appToast(response.message, TOAST_TYPE.SUCCESS);
        fetchPromptList(1); // Refresh the list from page one after deletion
      } catch (error) {
        console.error("Error deleting admin:", error);
        appToast("Failed to delete admin", TOAST_TYPE.ERROR);
      }
    }
  };

  const handleEditAdmin = (id) => {
    setAdminId(id);
    setModalOpen(true);
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <section className="content adminUser eventtype">
      <div className="container-fluid">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="row">
                <div className="col-lg-12">
                  <div className="card-top">
                    {isSuperAdmin && (
                      <div className="btn-group-right w-100">
                        {" "}
                        <button
                          onClick={() => {
                            setModalOpen(true);
                          }}
                          type="submit"
                          className="btn btn-blue btn-block float-end"
                        >
                          + Add Prompt
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-lg-12">
                  <div className="table-responsive">
                    <table
                      id="example"
                      className="table table-striped table-bordered"
                      cellSpacing="0"
                      width="100%"
                    >
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>Prompt Text</th>
                          <th>Default</th>
                          {isSuperAdmin && <th>Action</th>}
                        </tr>
                      </thead>
                      <tbody
                        style={{
                          display: tableLoading
                            ? "table-caption"
                            : "table-row-group",
                        }}
                      >
                        {tableLoading ? (
                          selectLoader(50)
                        ) : promptList.length > 0 ? (
                          promptList.map((prompt) => (
                            <tr key={prompt._id}>
                              <td>{prompt.name}</td>
                              <td
                                style={{
                                  maxWidth: "400px",
                                  whiteSpace: "pre-wrap",
                                }}
                              >
                                {prompt.promptText}
                              </td>
                              <td>
                                <label className="switch">
                                  <input
                                    type="checkbox"
                                    checked={prompt.isDefault}
                                    onChange={() =>
                                      handleToggle(prompt._id, prompt.isDefault)
                                    }
                                  />
                                  <span className="slider round"></span>
                                </label>
                              </td>
                              {isSuperAdmin && (
                                <td>
                                  <ul className="actions">
                                    <li>
                                      <button
                                        className="btn btn-pen"
                                        onClick={() =>
                                          handleEditAdmin(prompt._id)
                                        }
                                      >
                                        <i className="fas fa-pen"></i>
                                      </button>
                                    </li>
                                    <li>
                                      <button
                                        className="btn btn-trash"
                                        onClick={() => handleDelete(prompt._id)}
                                      >
                                        <i className="fas fa-trash"></i>
                                      </button>
                                    </li>
                                  </ul>
                                </td>
                              )}
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan="4">No prompts found</td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                    <ResponsivePagination
                      current={currentPage}
                      total={totalPages}
                      onPageChange={handlePageChange}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SidebarModal
          extraClass={"edit-profile-modal client_modal"}
          show={modalOpen}
          onHide={() => {
            setModalOpen(false);
            setAdminId("");
          }}
          headerTitle={"Add New Admin"}
          // headerTitle={member ? "Edit Team Member" : "Add Team Member"}
        >
          <AddEditUserPrompt
            closeModal={closeAndUpdateList}
            promptId={promptId}
          />
        </SidebarModal>
      </div>
    </section>
  );
};

export default UserPromptsList;
